import React, { Ref, useEffect, useState } from "react";
import { FastField } from "formik";
import { Fieldset, Form, Layout } from "@vwfs-bronson/bronson-react";
import { FormSection2 } from "../Sections/FormSection";
import { Output } from "../../Text";
import { renderRadioGroup, SelectField, TextField } from "../../formik";
import toOptions from "../../functional/toOptions";
import { DateField } from "../../formik/DateField";
import * as Validation from "../../validation";
import { Analytics } from '../../services/analytics';
import { trackAbort, trackFirstCustomerInteraction } from '../page.utils';

const select = (whitelist: string[], all: { key: string }[]) =>
  all.filter((v) => whitelist.includes(v.key));

const PersonalDetailsSectionFull = ({
  closed,
  success,
  showEditButton,
  onIconClick,
  form,
  buttons,
  containerRef,
}: {
  closed: boolean;
  success: boolean;
  showEditButton: boolean;
  onIconClick: (...args: any) => any;
  form: any;
  buttons: any;
  containerRef: Ref<any>;
}) => {
  const [changed, setChanged] = useState(false)
  const [lastTouchedField, setLastTouchedField] = useState('')

  const defaultTrackingData = {
    [Analytics.keys.pageName()]: 'Application form',
    [Analytics.keys.viewChange()]: 'Personal data',
    [Analytics.keys.formType()]: 'Personal data',
    [Analytics.keys.formName()]: 'Personal data'
  }

  const trackPage = () => {
    setTimeout(() => {
      const pageData = {
        [Analytics.keys.eventType(0)]: 'pageView',
        [Analytics.keys.eventAction(0)]: 'Success',
      }
      Analytics.setTrackingData({...defaultTrackingData, ...pageData})
      Analytics.trackPage()
    }, 1000)
  }

  const onBlur = (ev: any) => {
    if (!closed && ev && ev.target && ev.target.name) {
      setLastTouchedField(ev.target.name.split('.')[ev.target.name.split('.').length - 1])
      if (!changed) {
        setChanged(true)
        trackFirstCustomerInteraction(defaultTrackingData)
      }
    }
  }

  const onUnload = () => {
    if (!closed) {
      trackAbort(lastTouchedField, defaultTrackingData)
    }
  }

  const ownUnload = () => {
    window.addEventListener('unload', onUnload)
  }

  const desubscribeUnload = () => {
    window.removeEventListener('unload', onUnload)
  }

  useEffect(() => {
    if (!closed) {
      trackPage()
      ownUnload()
      return () => {
        desubscribeUnload()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closed])

  return (
    <FormSection2
      containerRef={containerRef}
      title={Output.resolve("pages.applicationForm.panels.personalData.title")}
      subtitle={Output.resolve(
        "pages.applicationForm.panels.personalData.subtitle"
      )}
      success={success}
      expanded={!closed}
      onHeaderClick={onIconClick}
      edit={
        !showEditButton
          ? undefined
          : {
              label: Output.resolve("pages.applicationForm.buttonEdit"),
              onClick: onIconClick,
            }
      }
    >
      <Form {...form} onBlur={onBlur} floatingLabel>
        <Fieldset>
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                {renderRadioGroup({
                  fieldName: "salutation",
                  legend: Output.resolve(
                    "pages.applicationForm.panels.personalData.fields.salutation.label"
                  ),
                  tooltip: undefined,
                  options: select(
                    ["MRS", "MR"],
                    Output.resolve(
                      "pages.applicationForm.panels.personalData.fields.salutation.values"
                    )
                  ),
                })}
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="title"
                  label={Output.resolve(
                    "pages.applicationForm.panels.personalData.fields.title.label"
                  )}
                  component={SelectField}
                >
                  {toOptions(
                    Output.resolve(
                      "pages.applicationForm.panels.personalData.fields.title.values",
                      []
                    )
                  )}
                </FastField>
              </Layout.Item>
            </Layout>
          </Fieldset.Row>
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="firstName"
                  label={Output.resolve(
                    "pages.applicationForm.panels.personalData.fields.firstName.label"
                  )}
                  component={TextField}
                />
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="lastName"
                  label={Output.resolve(
                    "pages.applicationForm.panels.personalData.fields.surname.label"
                  )}
                  component={TextField}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="maidenName"
                  label={Output.resolve(
                    "pages.applicationForm.panels.personalData.fields.maidenName.label"
                  )}
                  component={TextField}
                />
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="birthDate"
                  label={Output.resolve(
                    "pages.applicationForm.panels.personalData.fields.dateOfBirth.label"
                  )}
                  tooltip={Output.resolveAsMarkdown(
                    "pages.applicationForm.panels.personalData.fields.dateOfBirth.toolTip"
                  )}
                  component={DateField}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="placeOfBirth"
                  label={Output.resolve(
                    "pages.applicationForm.panels.personalData.fields.placeOfBirth.label"
                  )}
                  maxLength="30"
                  component={TextField}
                />
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="nationality"
                  label={Output.resolve(
                    "pages.applicationForm.panels.personalData.fields.citizenship.label"
                  )}
                  component={SelectField}
                >
                  {toOptions(Output.resolve("generic.countrylist", []))}
                </FastField>
              </Layout.Item>
            </Layout>
          </Fieldset.Row>
        </Fieldset>
        <Fieldset>
          <Fieldset.Row>{buttons}</Fieldset.Row>
        </Fieldset>
      </Form>
    </FormSection2>
  );
};

PersonalDetailsSectionFull.defaultProps = {
  validate: Validation.select(
    "title",
    "salutation",
    "firstName",
    "lastName",
    "birthDate",
    "maidenName",
    "placeOfBirth",
    "nationality",
  ),
};

export { PersonalDetailsSectionFull };
